import React, { lazy } from "react";
import { Router, Switch, Route, Redirect } from "react-router-dom";
import history from "./routerHistory";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "./fonts/AvenirNext/style.css";
import "./fonts/Poppins/style.css";
import "./fonts/ChakraPetch/style.css";
import "./fonts/Futura/style.css";
import "./fonts/Montserrat/style.css";
import { ToastContainer } from "react-toastify";
import GlobalStyle from "./style/Global";
import PageLoader from "./components/PageLoader";
import SuspenseWithChunkError from "./components/SuspenseWithChunkError";
import ScrollToTop from "components/ScrollToTop";
import { useFetchPublicData, useUser } from "state/hooks";
import { ProtectedRoute, PublicRoute } from "router";

const Home = lazy(() => import("./views/Home"));
const Signup = lazy(() => import("./views/Signup"));
const Login = lazy(() => import("./views/Login"));
const UserProfile = lazy(() => import("./views/UserProfile"));
const SocialLogin = lazy(() => import("./views/SocialLogin"));
const ResetPassword = lazy(() => import("./views/ResetPassword"));
const VerifyCode = lazy(() => import("./views/VerifyCode"));
const AccountSettings = lazy(() => import("./views/AccountSettings"));
const Dashboard = lazy(() => import("./views/Dashboard"));
const Marketplace = lazy(() => import("./views/Marketplace"));
const CreatorCollections = lazy(() => import("./views/CreatorCollections"));
const CreatorFrom = lazy(() => import("./views/CreatorForm"));
const CollectionForm = lazy(() => import("./views/CollectionForm"));
const CreateNft = lazy(() => import("./views/CreateNft"));
const ViewCollection = lazy(() => import("./views/ViewCollection"));
const Drops = lazy(() => import("./views/Drops"));
const CollectionDetail = lazy(() => import("./views/CollectionDetail"));
const BuyOpenEdition = lazy(() => import("./views/BuyOpenEdition"));
const NftDetail = lazy(() => import("./views/NftDetail"));
const GlobalOffer = lazy(() => import("./views/GlobalOffer"));
const PurchaseNft = lazy(() => import("./views/PurchaseNft"));
const PurchaseSuccess = lazy(() => import("./views/PurchaseSuccess"));
const Menu = lazy(() => import("components/Menu"));

const App: React.FC = () => {
  useFetchPublicData();
  const user = useUser();

  return (
    <Router history={history}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={true}
      />
      <GlobalStyle />
      <ScrollToTop />
      <SuspenseWithChunkError fallback={<PageLoader />}>
        <Menu>
          <Switch>
            <Route path={"/"} exact component={Home} />
            <Route path={"/auth/google/callback"} component={SocialLogin} />
            <Route path={"/auth/facebook/callback"} component={SocialLogin} />
            <Route path="/reset-password" component={ResetPassword} />
            <Route path={"/marketplace"} exact component={Marketplace} />
            <Route path={"/profile/:id"} exact component={UserProfile} />
            <Route path={"/collections"} exact component={Drops} />
            <Route
              path={"/collections/:id"}
              exact
              component={CollectionDetail}
            />
            <Route
              path={"/itemdetail/primary/1"}
              exact
              component={BuyOpenEdition}
            />
            <Route
              path={"/marketplace/group/:id"}
              exact
              component={NftDetail}
            />
            <Route path="/makeglobaloffer" component={GlobalOffer} />
            <Route path={"/purchase/:id"} exact component={PurchaseNft} />
            {/* Public ROUTES */}
            <PublicRoute path="/login" exact component={Login} />
            <PublicRoute path="/register" exact component={Signup} />
            <PublicRoute path="/verify" exact component={VerifyCode} />
            {/* Authenticated ROUTES */}
            <ProtectedRoute
              path={"/become-creator"}
              exact
              component={CreatorCollections}
            />
            <ProtectedRoute path={"/dashboard"} exact component={Dashboard} />
            <ProtectedRoute
              path={"/create/collection"}
              exact
              component={CollectionForm}
            />
            <ProtectedRoute path={"/create/nft"} component={CreateNft} />
            <ProtectedRoute
              exact
              path={"/purchase-success"}
              component={PurchaseSuccess}
            />
            <ProtectedRoute
              path={"/view-collection"}
              component={ViewCollection}
            />
            <ProtectedRoute
              path={"/settings"}
              exact
              component={AccountSettings}
            />
            <ProtectedRoute
              path={"/creator-form"}
              exact
              component={CreatorFrom}
            />

            {user && user.jwt && user.user.name ? (
              <Redirect from="*" to={"/"} exact />
            ) : (
              <Redirect from="*" to={"/login"} />
            )}
          </Switch>
        </Menu>
      </SuspenseWithChunkError>
    </Router>
  );
};

export default App;
